import type { DisplayJob } from '../types';
import type { JobFormState } from '../redux/features/jobFormSlice';
import type { ComparisonWeights } from './jobComparison';

export interface MatchScoreBreakdown {
  overall: number;
  salary: number;
  vacation: number;
  remoteness: number;
  benefits: number;
  languages: number;
  weights: ComparisonWeights;
  /** Only populated when the user has a desired/current title to compare against. */
  title?: number;
  roleLevel?: number;
  location?: number;
  jobType?: number;
}

/** Level hierarchy (en/fi/sv) for partial role-level matches. Keys are lowercased facet values. */
const ROLE_LEVEL_HIERARCHY: Record<string, number> = {
  intern: 1,
  harjoittelija: 1,
  praktikant: 1,
  junior: 2,
  'junior ja entry-taso': 2,
  'mid-level': 3,
  keskitaso: 3,
  medelnivå: 3,
  senior: 4,
  'senior-taso': 4,
  expert: 5,
  asiantuntija: 5,
  manager: 6,
  manageri: 6,
  chef: 6,
  director: 7,
  johtaja: 7,
  direktör: 7,
  executive: 8,
  johto: 8,
  företagsledare: 8,
};

function scoreTitle(jobTitle: string | undefined, jobNormalisedTitle: string | undefined, preferredTitles: string[]): number | undefined {
  const candidates = preferredTitles.map((t) => t.toLowerCase().trim()).filter(Boolean);
  if (candidates.length === 0) return undefined;
  const jTitleNorm = (jobNormalisedTitle || '').toLowerCase().trim();
  const jTitle = (jobTitle || '').toLowerCase().trim();
  if (!jTitleNorm && !jTitle) return undefined;

  let best = 0;
  for (const userTitle of candidates) {
    if (jTitleNorm && userTitle === jTitleNorm) {
      best = Math.max(best, 100);
      continue;
    }
    if (jTitleNorm && (jTitleNorm.includes(userTitle) || userTitle.includes(jTitleNorm))) {
      best = Math.max(best, 70);
      continue;
    }
    if (jTitle && userTitle === jTitle) {
      best = Math.max(best, 100);
      continue;
    }
    if (jTitle && (jTitle.includes(userTitle) || userTitle.includes(jTitle))) {
      best = Math.max(best, 60);
      continue;
    }
    const userWords = userTitle.split(/\s+/);
    const jobWords = (jTitleNorm || jTitle).split(/\s+/);
    const commonWords = userWords.filter((w) => jobWords.includes(w));
    if (commonWords.length > 0) {
      best = Math.max(best, 40);
    }
  }
  return best;
}

function scoreRoleLevel(jobRoleLevel: string | undefined, preferredLevels: string[]): number | undefined {
  const candidates = preferredLevels.map((l) => l.toLowerCase().trim()).filter(Boolean);
  const jobLevel = (jobRoleLevel || '').toLowerCase().trim();
  if (candidates.length === 0 || !jobLevel) return undefined;

  let best = 0;
  for (const userLevel of candidates) {
    if (userLevel === jobLevel) {
      best = Math.max(best, 100);
      continue;
    }
    const userLevelNum = ROLE_LEVEL_HIERARCHY[userLevel] || 0;
    const jobLevelNum = ROLE_LEVEL_HIERARCHY[jobLevel] || 0;
    if (userLevelNum > 0 && jobLevelNum > 0) {
      const diff = Math.abs(userLevelNum - jobLevelNum);
      if (diff === 0) best = Math.max(best, 100);
      else if (diff === 1) best = Math.max(best, 70);
      else if (diff === 2) best = Math.max(best, 40);
      else best = Math.max(best, 10);
    }
  }
  return best;
}

function scoreLocation(jobLocations: string[] | undefined, preferredLocations: string[]): number | undefined {
  const userLocations = preferredLocations.map((l) => l.toLowerCase().trim()).filter(Boolean);
  const jobLocs = (jobLocations ?? []).map((l) => l.toLowerCase().trim()).filter(Boolean);
  if (userLocations.length === 0 || jobLocs.length === 0) return undefined;

  const matches = jobLocs.filter((jl) => userLocations.some((ul) => jl.includes(ul) || ul.includes(jl)));
  if (matches.length === 0) return 0;
  return Math.min(100, Math.round((matches.length / jobLocs.length) * 100));
}

function scoreJobType(jobJobType: string | undefined, preferredJobTypes: string[]): number | undefined {
  const candidates = preferredJobTypes.map((t) => t.toLowerCase().trim()).filter(Boolean);
  const jobType = (jobJobType || '').toLowerCase().trim();
  if (candidates.length === 0 || !jobType) return undefined;
  return candidates.some((t) => t === jobType) ? 100 : 0;
}

/** Threshold-based salary scoring against a minimum acceptable salary (desired job salaryMin). */
function scoreSalaryAgainstMinimum(jobMin: number, jobMax: number, userMin: number): number {
  if (jobMin >= userMin) return 100;
  if (jobMax >= userMin) return 90;
  const diff = userMin - jobMax;
  const percentDiff = (diff / userMin) * 100;
  if (percentDiff <= 20) return 60;
  if (percentDiff <= 50) return 30;
  return 0;
}

/**
 * Calculate a match score (0-100) for a single job based on user profile and comparison weights.
 * This uses the same logic as jobComparison but adapted for single job scoring.
 * Returns both the overall score and the breakdown.
 */
export function calculateJobMatchScore(
  job: DisplayJob,
  userProfile: JobFormState,
  weights: ComparisonWeights
): MatchScoreBreakdown | null {
  // If user hasn't filled out their profile, return null (not zero)
  // This helps distinguish between "no profile" and "score is 0"
  if (!userProfile.currentJob || (!userProfile.currentJob.salary && !userProfile.currentJob.title && !userProfile.currentJob.normalisedTitle)) {
    return null;
  }

  const currentJob = userProfile.currentJob;
  const desiredJob = userProfile.desiredJob;
  const w: ComparisonWeights = {
    salaryWeight: weights.salaryWeight ?? 0.2,
    vacationWeight: weights.vacationWeight ?? 0.2,
    benefitsWeight: weights.benefitsWeight ?? 0.2,
    remotenessWeight: weights.remotenessWeight ?? 0.2,
    languagesWeight: (weights as ComparisonWeights & { languagesWeight?: number }).languagesWeight ?? 0.2,
    titleWeight: weights.titleWeight ?? 0.15,
    roleLevelWeight: weights.roleLevelWeight ?? 0.1,
    locationWeight: weights.locationWeight ?? 0.1,
    jobTypeWeight: weights.jobTypeWeight ?? 0.05,
  };

  // Convert user profile to DisplayJob format for comparison
  const userJob: DisplayJob = {
    objectID: 'user-current',
    title: currentJob.title || '',
    normalisedTitle: currentJob.normalisedTitle || '',
    normalisedRoleLevel: currentJob.normalisedRoleLevel || '',
    description: currentJob.description || '',
    requirements: [],
    responsibilities: [],
    skillsAndTechnologies: [],
    location: Array.isArray(currentJob.locations) ? currentJob.locations : [],
    remoteness: currentJob.remoteness || '',
    jobType: currentJob.jobType || '',
    // Profile: monthly salary + work %. Convert to yearly 100% FTE for comparison with job listings
    ...(function () {
      const monthly = typeof currentJob.salary === 'number' ? currentJob.salary : 0;
      const pct = typeof (currentJob as { workPercentage?: number }).workPercentage === 'number'
        ? Math.max(1, Math.min(100, (currentJob as { workPercentage: number }).workPercentage))
        : 100;
      const yearlyFte = monthly * 12 * (100 / pct);
      return { salaryRangeMin: yearlyFte, salaryRangeMax: yearlyFte };
    })(),
    salaryDetails: '',
    specialisations: [],
    requiredLanguages: [],
    optionalLanguages: [],
    postedDate: '',
    applicationDeadline: '',
    contactEmail: '',
    companyId: '',
    logoUrl: undefined,
    companyIndustry: undefined,
    employerOverview: undefined,
    companyContactDetails: undefined,
    benefits: Array.isArray(currentJob.benefits) ? currentJob.benefits : [],
    vacationWeeks: typeof currentJob.vacationWeeks === 'number' ? currentJob.vacationWeeks : 0,
    recruitmentProcess: [],
    educationLevel: '',
    minimumYearsOfExperience: 0,
    perks: [],
    url: '',
    processedAt: '',
    firstSeen: '',
    lastSeen: '',
    isActive: true,
    field: '',
    currency: 'EUR',
    score: 0,
  };

  // Use the same scoring logic as jobComparison but for single job comparison
  const allJobs = [userJob, job];

  // Salary: prefer a threshold check against desired minimum salary when set, else relative scaling
  const desiredSalaryMin = typeof desiredJob?.salaryMin === 'number' ? desiredJob.salaryMin : 0;
  const jobSalaryMin = typeof job.salaryRangeMin === 'number' ? job.salaryRangeMin : 0;
  const jobSalaryMax = typeof job.salaryRangeMax === 'number' ? job.salaryRangeMax : jobSalaryMin;
  let salaryScore: number;
  if (desiredSalaryMin > 0) {
    salaryScore = scoreSalaryAgainstMinimum(jobSalaryMin, jobSalaryMax, desiredSalaryMin);
  } else {
    const salaryMins = allJobs.map((j) => (typeof j.salaryRangeMin === 'number' ? j.salaryRangeMin : 0));
    const salaryMaxs = allJobs.map((j) => (typeof j.salaryRangeMax === 'number' ? j.salaryRangeMax : 0));
    const maxSalary = Math.max(...salaryMaxs, 0);
    const minSalary = Math.min(...salaryMins, 0);
    const salaryRange = maxSalary - minSalary || 1;
    salaryScore = ((jobSalaryMin - minSalary) / salaryRange) * 100;
    if (!isFinite(salaryScore) || isNaN(salaryScore)) salaryScore = 0;
    // If job salary is higher than user's current, give bonus
    if (jobSalaryMin > userJob.salaryRangeMin) {
      salaryScore = Math.min(100, salaryScore + 20);
    }
  }

  // Vacation
  const vacationWeeksArr = allJobs.map((j) => (typeof j.vacationWeeks === 'number' ? j.vacationWeeks : 0));
  const maxVacation = Math.max(...vacationWeeksArr, 0);
  const minVacation = Math.min(...vacationWeeksArr, 0);
  const vacationRange = maxVacation - minVacation || 1;
  const jobVacation = typeof job.vacationWeeks === 'number' ? job.vacationWeeks : 0;
  let vacationScore = ((jobVacation - minVacation) / vacationRange) * 100;
  if (!isFinite(vacationScore) || isNaN(vacationScore)) vacationScore = 0;
  // If job vacation is more than user's current, give bonus
  if (jobVacation >= userJob.vacationWeeks) {
    vacationScore = Math.min(100, vacationScore + 30);
  }

  // Remoteness: prefer desired remoteness options when set, else current job's remoteness
  const desiredRemoteness = (desiredJob?.remoteness ?? []).map((r) => r.toLowerCase().trim()).filter(Boolean);
  const jobRemoteness = String(job.remoteness ?? '').toLowerCase().trim();
  let remotenessScore = 0;
  if (desiredRemoteness.length > 0) {
    remotenessScore = Math.max(
      0,
      ...desiredRemoteness.map((r) =>
        r === jobRemoteness ? 100 : r === 'hybrid' || jobRemoteness === 'hybrid' ? 60 : 0
      )
    );
  } else if (jobRemoteness && typeof userJob.remoteness === 'string' && userJob.remoteness) {
    const userRemoteness = userJob.remoteness.toLowerCase();
    if (jobRemoteness === userRemoteness) {
      remotenessScore = 100;
    } else if (userRemoteness === 'hybrid' || jobRemoteness === 'hybrid') {
      remotenessScore = 60;
    } else if (userRemoteness === 'onsite' && jobRemoteness === 'remote') {
      remotenessScore = 80; // Remote is generally preferred
    } else {
      remotenessScore = 0;
    }
  }
  if (!isFinite(remotenessScore) || isNaN(remotenessScore)) remotenessScore = 0;

  // Benefits
  const benefitsArr = allJobs.map((j) => (Array.isArray(j.benefits) ? j.benefits.length : 0));
  const maxBenefits = Math.max(...benefitsArr, 0);
  const minBenefits = Math.min(...benefitsArr, 0);
  const benefitsRange = maxBenefits - minBenefits || 1;
  const jobBenefits = Array.isArray(job.benefits) ? job.benefits.length : 0;
  let benefitsScore = ((jobBenefits - minBenefits) / benefitsRange) * 100;
  if (!isFinite(benefitsScore) || isNaN(benefitsScore)) benefitsScore = 0;
  // If job has more benefits than user's current, give bonus
  if (jobBenefits > (Array.isArray(userJob.benefits) ? userJob.benefits.length : 0)) {
    benefitsScore = Math.min(100, benefitsScore + 20);
  }

  // Languages: compare user's professional languages with job required/optional
  const userLangs = (currentJob.languages ?? []).map((l) => String(l).toLowerCase().trim()).filter(Boolean);
  const required = Array.isArray(job.requiredLanguages) ? job.requiredLanguages : [];
  const optional = Array.isArray(job.optionalLanguages) ? job.optionalLanguages : [];
  let languagesScore = 100;
  if (required.length > 0) {
    const requiredMet = required.filter((r) => userLangs.some((u) => u === String(r).toLowerCase().trim())).length;
    languagesScore = (requiredMet / required.length) * 100;
  } else if (optional.length > 0) {
    const optionalMet = optional.filter((o) => userLangs.some((u) => u === String(o).toLowerCase().trim())).length;
    languagesScore = optionalMet / optional.length * 100;
  }
  if (!isFinite(languagesScore) || isNaN(languagesScore)) languagesScore = 100;

  // Title / role level / location / job type: prefer desired-job preferences, fall back to current job
  const preferredTitles = desiredJob?.titles?.length
    ? desiredJob.titles
    : [currentJob.title, currentJob.normalisedTitle].filter((t): t is string => Boolean(t));
  const titleScore = scoreTitle(job.title, job.normalisedTitle, preferredTitles);

  const preferredRoleLevels = desiredJob?.normalisedRoleLevels?.length
    ? desiredJob.normalisedRoleLevels
    : currentJob.normalisedRoleLevel
      ? [currentJob.normalisedRoleLevel]
      : [];
  const roleLevelScore = scoreRoleLevel(job.normalisedRoleLevel, preferredRoleLevels);

  const preferredLocations = desiredJob?.locations?.length ? desiredJob.locations : currentJob.locations ?? [];
  const locationScore = scoreLocation(job.location, preferredLocations);

  const preferredJobTypes = desiredJob?.jobTypes?.length ? desiredJob.jobTypes : currentJob.jobType ? [currentJob.jobType] : [];
  const jobTypeScore = scoreJobType(job.jobType, preferredJobTypes);

  // Calculate weighted overall score. Optional dimensions only contribute when they have a value,
  // and their weight is redistributed proportionally across the dimensions that do.
  const dims: Array<{ score: number | undefined; weight: number }> = [
    { score: salaryScore, weight: w.salaryWeight },
    { score: vacationScore, weight: w.vacationWeight },
    { score: remotenessScore, weight: w.remotenessWeight },
    { score: benefitsScore, weight: w.benefitsWeight },
    { score: languagesScore, weight: w.languagesWeight },
    { score: titleScore, weight: w.titleWeight ?? 0 },
    { score: roleLevelScore, weight: w.roleLevelWeight ?? 0 },
    { score: locationScore, weight: w.locationWeight ?? 0 },
    { score: jobTypeScore, weight: w.jobTypeWeight ?? 0 },
  ];
  const activeDims = dims.filter((d) => typeof d.score === 'number');
  const totalActiveWeight = activeDims.reduce((sum, d) => sum + d.weight, 0);
  let overallScore = 0;
  if (totalActiveWeight > 0) {
    overallScore = activeDims.reduce((sum, d) => sum + (d.score as number) * d.weight, 0) / totalActiveWeight;
  }
  if (!isFinite(overallScore) || isNaN(overallScore)) overallScore = 0;

  return {
    overall: Math.max(0, Math.min(100, Math.round(overallScore))),
    salary: Math.round(salaryScore),
    vacation: Math.round(vacationScore),
    remoteness: Math.round(remotenessScore),
    benefits: Math.round(benefitsScore),
    languages: Math.round(languagesScore),
    weights: w,
    ...(typeof titleScore === 'number' ? { title: Math.round(titleScore) } : {}),
    ...(typeof roleLevelScore === 'number' ? { roleLevel: Math.round(roleLevelScore) } : {}),
    ...(typeof locationScore === 'number' ? { location: Math.round(locationScore) } : {}),
    ...(typeof jobTypeScore === 'number' ? { jobType: Math.round(jobTypeScore) } : {}),
  };
}
